import { ChildComponent, ComponentIds, DisplayNameComponent, HierarchyUtils } from '@inter-ikea-core/core';
import { FrameGroupComponent, PlannerEcsMultiSystem, PlannerProductComponent, ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
/**
 * Keeps track of grouped frames.
 */
export class PaxFrameGroupingSystem extends PlannerEcsMultiSystem {
    constructor(coreApi) {
        super(coreApi, 'PaxFrameGroupingSystem', [
            ComponentIds.IN_SCENE_COMPONENT_ID,
            ComponentIds.IS_DRAGGED_COMPONENT_ID
        ]);
        this.mandatoryComponents = [PlannerProductComponent.id];
    }
    onAdd(entity, newComponent) {
        if (newComponent.typeId === ComponentIds.IN_SCENE_COMPONENT_ID) {
            if (this.isFrame(entity)) {
                // If frame already exists in a group, ignore it. We get double add events so we need to check this
                if (this.entityExistsInGroup(entity))
                    return;
                if (this.isPartOfCornerSolution(entity)) {
                    const { measurementFrame } = this.getCornerSolutionFrames(entity);
                    if (measurementFrame) {
                        this.addToGroups(measurementFrame);
                    }
                }
                this.addToGroups(entity);
            }
            else if (this.coreApi.entities.isProductType(entity, ProductType.PAX_CORNER_SOLUTION_ASM)) {
                const { mainFrame, addonFrame, measurementFrame } = this.getCornerSolutionFrames(entity);
                if (addonFrame) {
                    this.addToGroups(addonFrame);
                }
                if (mainFrame) {
                    this.addToGroups(mainFrame);
                }
                if (measurementFrame) {
                    this.addToGroups(measurementFrame);
                }
            }
        }
    }
    onUpdate() { }
    onRemove(entity, oldComponent) {
        const eventType = oldComponent.typeId;
        switch (eventType) {
            case ComponentIds.IN_SCENE_COMPONENT_ID:
                if (this.isCornerSolution(entity)) {
                    const { mainFrame, addonFrame, measurementFrame } = this.getCornerSolutionFrames(entity);
                    if (mainFrame) {
                        this.removeFromGroups(mainFrame);
                    }
                    if (addonFrame) {
                        this.removeFromGroups(addonFrame);
                    }
                    if (measurementFrame) {
                        this.removeFromGroups(measurementFrame);
                    }
                }
                else {
                    this.removeFromGroups(entity);
                }
                break;
            case ComponentIds.IS_DRAGGED_COMPONENT_ID:
                if (this.isCornerSolution(entity)) {
                    const { mainFrame, addonFrame, measurementFrame } = this.getCornerSolutionFrames(entity);
                    if (mainFrame) {
                        this.removeFromGroups(mainFrame);
                        this.addToGroups(mainFrame);
                    }
                    if (addonFrame) {
                        this.removeFromGroups(addonFrame);
                        this.addToGroups(addonFrame);
                    }
                    if (measurementFrame) {
                        this.removeFromGroups(measurementFrame);
                        this.addToGroups(measurementFrame);
                    }
                }
                else {
                    this.removeFromGroups(entity);
                    this.addToGroups(entity);
                }
                break;
        }
    }
    addToGroups(entity) {
        const alignedFrameGroup = this.getAlignedFrameGroup(entity);
        if (alignedFrameGroup.alignedFrames?.left && alignedFrameGroup.alignedFrames.right) {
            this.combineGroups(entity, alignedFrameGroup.alignedFrames);
        }
        else if (alignedFrameGroup.group) {
            this.addEntityToExistingGroups(entity, alignedFrameGroup.group, alignedFrameGroup.alignedFrames);
        }
        else {
            this.createNewFrameGroupEntity([entity]);
        }
    }
    /**
     * Removes entity from groups.
     * @param entity Entity to remove
     */
    removeFromGroups(entity) {
        this.coreApi.entities.getByComponent(FrameGroupComponent.id).forEach(groupEntity => {
            const group = groupEntity.getComponent(FrameGroupComponent.id);
            if (group) {
                if (group.frames.some(e => entity.id === e.id)) {
                    if (group.frames.length === 1) {
                        groupEntity.dispose();
                        this.coreApi.entities.delete(groupEntity);
                    }
                    else {
                        this.splitGroup(entity, group, groupEntity);
                    }
                }
            }
        });
    }
    isFrame(entity) {
        return entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID);
    }
    isCornerSolution(entity) {
        return this.coreApi.entities.isProductType(entity, ProductType.PAX_CORNER_SOLUTION_ASM);
    }
    isPartOfCornerSolution(entity) {
        return HierarchyUtils.findInUpwardsHierarchy(entity, e => e.getComponent(ProductInfoComponent.id)?.productType ===
            ProductType.PAX_CORNER_SOLUTION_ASM);
    }
    getCornerSolutionFrames(entity) {
        const addonFrame = HierarchyUtils.descendants(entity).find(it => this.coreApi.entities.isProductType(it, ProductType.PAX_ADD_ON_CORNER_FRAME));
        const mainFrame = HierarchyUtils.descendants(entity).find(it => this.coreApi.entities.isProductType(it, ProductType.PAX_FRAME));
        const measurementFrame = HierarchyUtils.descendants(entity).find(it => this.coreApi.entities.isProductType(it, ProductType.PAX_CORNER_MEASUREMENT_FRAME));
        return { addonFrame, mainFrame, measurementFrame };
    }
    addEntityToExistingGroups(entity, alignedFrameGroup, alignedFrames) {
        const frameIdsInGroup = alignedFrameGroup.getComponent(FrameGroupComponent.id)?.frames;
        if (frameIdsInGroup && !frameIdsInGroup.includes(entity)) {
            const frameIdsInGroupCopy = [...frameIdsInGroup];
            if (alignedFrames?.left) {
                this.addFramesToGroup(alignedFrameGroup, frameIdsInGroupCopy.splice(frameIdsInGroupCopy.indexOf(alignedFrames.left) + 1, 0, entity));
            }
            else if (alignedFrames?.right) {
                this.addFramesToGroup(alignedFrameGroup, frameIdsInGroupCopy.splice(frameIdsInGroupCopy.indexOf(alignedFrames.right), 0, entity));
            }
            this.addFramesToGroup(alignedFrameGroup, frameIdsInGroupCopy);
        }
    }
    addFramesToGroup(groupEntity, frames) {
        const cornerSolutionIndex = frames.findIndex(f => this.isPartOfCornerSolution(f));
        groupEntity.addComponent(new FrameGroupComponent(frames, cornerSolutionIndex >= 0));
    }
    createNewFrameGroupEntity(frames) {
        const scene = this.coreApi.entities.getScene();
        if (!scene)
            return;
        const coreServices = this.coreApi.getServices();
        const newFrameGroup = coreServices.entityManager.createEntity();
        newFrameGroup.addComponent(new DisplayNameComponent('Frame Group'));
        newFrameGroup.addComponent(new ChildComponent(scene));
        this.addFramesToGroup(newFrameGroup, frames);
    }
    entityExistsInGroup(entity) {
        return this.coreApi.entities
            .getByComponent(FrameGroupComponent.id)
            .some(e => e.getComponent(FrameGroupComponent.id)?.frames.includes(entity));
    }
    getAlignedFrameGroup(entity) {
        const alignedFrames = this.coreApi.entities.getAlignedFrames(entity, 'align-left', 'align-right');
        // Frame has alignments on both sides, should combine groups
        if (alignedFrames.left || alignedFrames.right) {
            const alignedEntity = alignedFrames.left ? alignedFrames.left : alignedFrames.right;
            if (alignedEntity) {
                const group = this.getGroup(alignedEntity);
                if (group) {
                    return {
                        group,
                        alignedFrames
                    };
                }
            }
        }
        return { group: undefined };
    }
    getGroup(entity) {
        const groupEntities = this.coreApi.entities.getByComponent(FrameGroupComponent.id);
        return groupEntities.find(g => g.getComponent(FrameGroupComponent.id)?.frames.includes(entity));
    }
    combineGroups(entity, alignedFrames) {
        if (alignedFrames.left && alignedFrames.right) {
            const leftGroupEntity = this.getGroup(alignedFrames.left);
            const leftGroupFrames = leftGroupEntity?.getComponent(FrameGroupComponent.id)?.frames;
            const rightGroupEntity = this.getGroup(alignedFrames.right);
            const rightGroupFrames = rightGroupEntity?.getComponent(FrameGroupComponent.id)?.frames;
            let newFrameList = [];
            if (leftGroupFrames) {
                newFrameList = leftGroupFrames;
            }
            if (rightGroupFrames) {
                newFrameList = [...newFrameList, entity, ...rightGroupFrames];
            }
            this.createNewFrameGroupEntity(newFrameList);
            leftGroupEntity && this.coreApi.entities.delete(leftGroupEntity);
            rightGroupEntity && this.coreApi.entities.delete(rightGroupEntity);
        }
    }
    /**
     * Split a group into 2.
     * @param entityId Id of current entity
     * @param frameGroupComponent The frame group component current entity is in
     * @param groupEntity The entity holding the frame group.
     */
    splitGroup(entity, frameGroupComponent, groupEntity) {
        const groupIndex = frameGroupComponent.frames.findIndex(e => entity.id === e.id);
        // If current entity is somewhere inside the group and it needs to be split.
        if (groupIndex > 0 && groupIndex < frameGroupComponent.frames.length - 1) {
            const p1 = [...frameGroupComponent.frames].slice(0, groupIndex);
            const p2 = [...frameGroupComponent.frames].splice(groupIndex + 1, 1);
            this.addFramesToGroup(groupEntity, p1);
            this.createNewFrameGroupEntity(p2);
        }
        else {
            // Else just remove the id since it is on one of the ends in the group
            this.addFramesToGroup(groupEntity, frameGroupComponent.frames.filter(e => e.id !== entity.id));
        }
    }
}
//# sourceMappingURL=PaxFrameGroupingSystem.js.map